import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, youtubeEmbed } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAppText } from "@/contexts/AppTextContext";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Lock, MessageSquare, Send, Upload, Trophy, Pencil, Trash2, X, ChevronDown, ChevronRight } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;

// very small markdown renderer for headings, bold, lists, blockquote, paragraphs (legacy support)
function renderMarkdown(text) {
  if (!text) return "";
  const lines = text.split("\n");
  const out = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("# ")) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h1>${line.slice(2)}</h1>`); }
    else if (line.startsWith("## ")) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h2>${line.slice(3)}</h2>`); }
    else if (line.startsWith("### ")) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h3>${line.slice(4)}</h3>`); }
    else if (line.startsWith("> ")) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<blockquote>${line.slice(2)}</blockquote>`); }
    else if (line.match(/^[-*] /)) { if (!inList) { out.push("<ul>"); inList = true; } out.push(`<li>${line.slice(2)}</li>`); }
    else if (line.trim() === "") { if (inList) { out.push("</ul>"); inList = false; } }
    else { if (inList) { out.push("</ul>"); inList = false; } out.push(`<p>${line}</p>`); }
  }
  if (inList) out.push("</ul>");
  let html = out.join("\n");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  return html;
}

// Detect if content is already HTML (from rich editor) or legacy markdown
function renderContent(text) {
  if (!text) return "";
  const trimmed = text.trim();
  if (trimmed.startsWith("<") && /<\/?[a-z][\s\S]*>/i.test(trimmed)) {
    // Replace &nbsp; with regular space so text wraps naturally
    return trimmed.replace(/&nbsp;/g, " ");
  }
  return renderMarkdown(text);
}

function SidebarLessonList({ lessons, currentId, completed, collapsedSections, setCollapsedSections, selectLesson, t, isStaff }) {
  const sectionNames = [...new Set(lessons.filter(l => l.section).map(l => l.section))];
  const groups = [
    ...lessons.filter(l => !l.section).map(l => ({ type: "lesson", lesson: l, order: l.order })),
    ...sectionNames.map(name => {
      const items = lessons.filter(l => l.section === name);
      return { type: "section", name, items, order: Math.min(...items.map(l => l.order)) };
    }),
  ].sort((a, b) => a.order - b.order);

  // flat ordered list: sections sorted by creation order (from API), lessons sequential within each section
  const orderedIds = groups.flatMap(g => g.type === "lesson" ? [g.lesson.lesson_id] : g.items.map(l => l.lesson_id));
  const isUnlocked = (id) => {
    if (isStaff) return true;
    const pos = orderedIds.indexOf(id);
    if (pos === 0) return true;
    return completed.has(orderedIds[pos - 1]);
  };

  let lessonIdx = 0;
  return groups.map((g) => {
    if (g.type === "lesson") {
      const idx = lessonIdx++;
      const unlocked = isUnlocked(g.lesson.lesson_id);
      const isCurrent = g.lesson.lesson_id === currentId;
      return (
        <div key={g.lesson.lesson_id} className="relative group">
          <button onClick={() => unlocked && selectLesson(g.lesson.lesson_id)}
            className={`w-full text-left px-3 py-3 rounded-xl flex items-start gap-3 transition-colors ${isCurrent ? "bg-[#F4F2F8]" : unlocked ? "hover:bg-[#F4F2F8]/60" : "opacity-50 cursor-not-allowed"}`}
            data-testid={`lesson-item-${g.lesson.lesson_id}`}>
            {completed.has(g.lesson.lesson_id)
              ? <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
              : unlocked
                ? <Circle className="w-5 h-5 text-[#8B8794] mt-0.5 flex-shrink-0" />
                : <Lock className="w-5 h-5 text-[#8B8794] mt-0.5 flex-shrink-0" />}
            <div className="min-w-0">
              <div className="text-xs text-[#8B8794]">{t("course_learn.lesson_prefix")} {idx + 1}</div>
              <div className="text-sm text-[#1A1422] line-clamp-2">{g.lesson.title}</div>
            </div>
          </button>
          {!unlocked && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover:block z-50 pointer-events-none">
              <div className="bg-[#1A1422] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                Selesaikan materi sebelumnya dulu
              </div>
            </div>
          )}
        </div>
      );
    }
    const isOpen = !collapsedSections[g.name];
    const doneInSection = g.items.filter(l => completed.has(l.lesson_id)).length;
    return (
      <div key={g.name}>
        <button onClick={() => setCollapsedSections(s => ({ ...s, [g.name]: !s[g.name] }))}
          className="w-full text-left px-3 py-2.5 rounded-xl flex items-center justify-between gap-2 hover:bg-[#F4F2F8]/60 transition-colors">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold bg-[#FF8C00]/10 text-[#FF8C00] px-2 py-0.5 rounded-full flex-shrink-0">MODUL</span>
            <span className="text-sm font-semibold text-[#1A1422] truncate">{g.name}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[10px] text-[#8B8794]">{doneInSection}/{g.items.length}</span>
            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-[#8B8794]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#8B8794]" />}
          </div>
        </button>
        {g.items.map((l) => {
          const idx = lessonIdx++;
          if (!isOpen) return null;
          const unlocked = isUnlocked(l.lesson_id);
          return (
            <div key={l.lesson_id} className="relative group">
              <button onClick={() => unlocked && selectLesson(l.lesson_id)}
                className={`w-full text-left pl-6 pr-3 py-2.5 rounded-xl flex items-start gap-3 transition-colors ${l.lesson_id === currentId ? "bg-[#F4F2F8]" : unlocked ? "hover:bg-[#F4F2F8]/60" : "opacity-50 cursor-not-allowed"}`}
                data-testid={`lesson-item-${l.lesson_id}`}>
              {completed.has(l.lesson_id)
                ? <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                : unlocked
                  ? <Circle className="w-4 h-4 text-[#8B8794] mt-0.5 flex-shrink-0" />
                  : <Lock className="w-4 h-4 text-[#8B8794] mt-0.5 flex-shrink-0" />}
              <div className="min-w-0">
                <div className="text-xs text-[#8B8794]">{t("course_learn.lesson_prefix")} {idx + 1}</div>
                <div className="text-sm text-[#1A1422] line-clamp-2">{l.title}</div>
              </div>
            </button>
            {!unlocked && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover:block z-50 pointer-events-none">
                <div className="bg-[#1A1422] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                  Selesaikan materi sebelumnya dulu
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    );
  });
}

export default function CourseLearn() {
  const { courseId } = useParams();
  const [search, setSearch] = useSearchParams();
  const { user } = useAuth();
  const { t } = useAppText();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // load
  useEffect(() => {
    (async () => {
      try {
        const [c, l, e] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/courses/${courseId}/lessons`),
          api.get(`/me/enrollments`),
        ]);
        setCourse(c.data);
        setLessons(l.data);
        const my = e.data.find((x) => x.course.course_id === courseId);
        if (my) setCompleted(new Set(my.completed_lessons));
        const initial = search.get("lesson") || l.data[0]?.lesson_id;
        setCurrentId(initial);
      } catch (e) {
        toast.error("Tidak bisa memuat materi");
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId]);

  const current = lessons.find((l) => l.lesson_id === currentId);

  const selectLesson = (id) => {
    setCurrentId(id);
    setSearch({ lesson: id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const markComplete = async () => {
    if (!current) return;
    try {
      await api.post(`/lessons/${current.lesson_id}/complete`);
      setCompleted((s) => new Set([...s, current.lesson_id]));
      toast.success("Materi ditandai selesai");
    } catch {
      toast.error("Gagal menandai materi");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto p-10 text-[#5C5868]">{t("dashboard.loading")}</div>;
  if (!course) return <div className="p-10 text-[#5C5868]">Kursus tidak ditemukan</div>;

  const progress = lessons.length ? Math.round((completed.size / lessons.length) * 100) : 0;
  const isStaff = ["admin", "coach"].includes(user?.role);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="course-learn-page">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5C5868] hover:text-[#1A1422]">
          <ArrowLeft className="w-4 h-4" /> {t("course_learn.back_link")}
        </Link>
        {/* Tombol sidebar untuk mobile/tablet */}
        <button
          className="lg:hidden flex items-center gap-2 text-sm font-semibold text-[#FF8C00] border border-[#FF8C00]/30 rounded-full px-3 py-1.5"
          onClick={() => setSidebarOpen(o => !o)}
        >
          <span>📋</span> Daftar Materi
        </button>
      </div>

      {/* Sidebar overlay mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" style={{zIndex:0}} onClick={() => setSidebarOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-sm bg-white overflow-y-auto p-4 shadow-2xl" style={{zIndex:1,WebkitOverflowScrolling:"touch"}}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-bold text-[#1A1422]">{course.title}</span>
              <button onClick={() => setSidebarOpen(false)}><X className="w-5 h-5 text-[#8B8794]" /></button>
            </div>
            <div className="h-1.5 bg-[#F4F2F8] rounded-full overflow-hidden mb-1">
              <div className="h-full bg-[#FF8C00]" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-[#5C5868] mb-4">{completed.size}/{lessons.length} {t("catalog.lessons_suffix")} • {progress}%</div>
            {progress === 100 && (
              <div className="flex items-center gap-2 text-[#FF8C00] text-sm font-semibold mb-3">
                <Trophy className="w-4 h-4" /> {t("course_learn.graduated_msg")}
              </div>
            )}
            <SidebarLessonList
              lessons={lessons} currentId={currentId} completed={completed}
              collapsedSections={collapsedSections} setCollapsedSections={setCollapsedSections}
              selectLesson={(id) => { selectLesson(id); setSidebarOpen(false); }}
              t={t} isStaff={isStaff}
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[300px_1fr] gap-6 min-w-0 w-full">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block lg:sticky lg:top-20 self-start">
          <div className="card-surface p-5 mb-4">
            <div className="text-xs text-[#5C5868] mb-1">{t("course_learn.course_label")}</div>
            <h2 className="font-heading font-bold text-[#1A1422] text-lg mb-3 line-clamp-2">{course.title}</h2>
            <div className="h-1.5 bg-[#F4F2F8] rounded-full overflow-hidden mb-1">
              <div className="h-full bg-[#FF8C00]" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-[#5C5868]">{completed.size}/{lessons.length} {t("catalog.lessons_suffix")} • {progress}%</div>
            {progress === 100 && (
              <div className="mt-3 flex items-center gap-2 text-[#FF8C00] text-sm font-semibold">
                <Trophy className="w-4 h-4" /> {t("course_learn.graduated_msg")}
              </div>
            )}
          </div>
          <div className="card-surface p-3">
            <SidebarLessonList
              lessons={lessons} currentId={currentId} completed={completed}
              collapsedSections={collapsedSections} setCollapsedSections={setCollapsedSections}
              selectLesson={selectLesson} t={t} isStaff={isStaff}
            />
          </div>
        </aside>

        {/* Lesson content */}
        <main className="min-w-0">
          {current && (
            <div className="space-y-6">
              <div className="card-surface p-5 sm:p-8 min-w-0">
                <div className="text-xs text-[#5C5868] mb-2 uppercase tracking-wider">{t("course_learn.section_label")}</div>
                <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#1A1422] mb-5" data-testid="lesson-title">
                  {current.title}
                </h1>
                {current.video_url && (
                  <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black mb-5">
                    <iframe
                      src={youtubeEmbed(current.video_url)}
                      title={current.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
                {current.content && (
                  <div className="rich-content" lang="id" dangerouslySetInnerHTML={{ __html: renderContent(current.content) }} />
                )}
                <div className="flex flex-wrap gap-3 mt-6">
                  {!completed.has(current.lesson_id) ? (
                    <button onClick={markComplete} className="btn-primary" data-testid="mark-complete-button">
                      <CheckCircle2 className="w-4 h-4" /> {t("course_learn.mark_complete")}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[#10B981] font-semibold">
                      <CheckCircle2 className="w-5 h-5" /> {t("course_learn.completed_label")}
                    </span>
                  )}
                </div>
              </div>

              {current.has_assignment && (
                <AssignmentBlock lesson={current} userName={user?.name} t={t} />
              )}

              <CommentsBlock lessonId={current.lesson_id} userName={user?.name} userPicture={user?.picture} t={t} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function AssignmentBlock({ lesson, t }) {
  const [content, setContent] = useState("");
  const [submission, setSubmission] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/lessons/${lesson.lesson_id}/my-submission`);
    if (data && data.submission_id) setSubmission(data);
    else setSubmission(null);
  }, [lesson.lesson_id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.lesson_id}/submit`, { content });
      setSubmission(data);
      setContent("");
      toast.success("Tugas terkirim!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = () => {
    setEditContent(submission.content);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editContent.replace(/<[^>]*>/g, "").trim()) return toast.error("Jawaban tidak boleh kosong");
    setSubmitting(true);
    try {
      const { data } = await api.put(`/me/submissions/${submission.submission_id}`, { content: editContent });
      setSubmission(data);
      setEditing(false);
      toast.success("Jawaban diperbarui");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSubmission = async () => {
    if (!window.confirm("Hapus jawaban ini? Kamu bisa kirim ulang setelah dihapus.")) return;
    setDeleting(true);
    try {
      await api.delete(`/me/submissions/${submission.submission_id}`);
      setSubmission(null);
      setContent("");
      toast.success("Jawaban dihapus");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    } finally {
      setDeleting(false);
    }
  };

  const isGraded = submission?.grade != null;

  return (
    <div className="card-surface p-6 sm:p-8" data-testid="assignment-block">
      <div className="flex items-center gap-2 mb-2">
        <Upload className="w-5 h-5 text-[#FF8C00]" />
        <h3 className="font-heading text-xl font-bold text-[#1A1422]">{t("course_learn.assignment_title_prefix")} {lesson.assignment_title || lesson.title}</h3>
      </div>
      {lesson.assignment_description && (
        <p className="text-[#5C5868] mb-4 whitespace-pre-line">{lesson.assignment_description}</p>
      )}

      {submission && (
        <div className="bg-[#F4F2F8] rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-[#5C5868]">{t("course_learn.submitted_prefix")} {new Date(submission.submitted_at).toLocaleString("id-ID")}</div>
            {!isGraded && !editing && (
              <div className="flex items-center gap-1">
                <button onClick={startEdit} className="flex items-center gap-1 text-xs text-[#378ADD] hover:underline px-2 py-1 rounded hover:bg-[#E6F1FB]">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button onClick={deleteSubmission} disabled={deleting} className="flex items-center gap-1 text-xs text-[#E24B4A] hover:underline px-2 py-1 rounded hover:bg-[#FCEBEB]">
                  <Trash2 className="w-3 h-3" /> {deleting ? "..." : "Hapus"}
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div>
              <RichTextEditor value={editContent} onChange={setEditContent} placeholder="Edit jawaban kamu..." testId="assignment-edit-textarea" />
              <div className="flex gap-2 mt-3">
                <button onClick={saveEdit} disabled={submitting} className="btn-primary text-sm">
                  {submitting ? "Menyimpan..." : "Simpan"}
                </button>
                <button onClick={() => setEditing(false)} className="btn-ghost text-sm flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Batal
                </button>
              </div>
            </div>
          ) : (
            <div className="text-[#1A1422] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: submission.content }} />
          )}

          {(isGraded || submission.feedback) && (
            <div className="mt-3 pt-3 border-t border-[#E8E5EE] space-y-2">
              {isGraded && (
                <div className="text-xs text-[#FF8C00] font-semibold">Nilai: {submission.grade}/100</div>
              )}
              {submission.feedback && (
                <div className="bg-[#FFF4E6] border border-[#FFD8A8] rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    {submission.graded_by_picture ? (
                      <img src={toAbsUrl(submission.graded_by_picture)} className="w-6 h-6 rounded-full object-cover" alt="" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#FF8C00] flex items-center justify-center text-white text-[10px] font-bold">
                        {submission.graded_by_name?.[0] || "?"}
                      </div>
                    )}
                    <span className="text-xs font-semibold text-[#FF8C00]">{submission.graded_by_name || "BisaUsaha"}</span>
                    {submission.graded_by_role && (
                      <span className="text-[10px] text-[#8B8794] capitalize">· {submission.graded_by_role}</span>
                    )}
                    {submission.graded_at && (
                      <span className="text-[10px] text-[#8B8794] ml-auto">{new Date(submission.graded_at).toLocaleString("id-ID")}</span>
                    )}
                  </div>
                  <p className="text-sm text-[#1A1422]">{submission.feedback}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!editing && (
        <>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder={submission ? t("course_learn.submission_resend_placeholder") : t("course_learn.submission_placeholder")}
            testId="assignment-textarea"
          />
          <button onClick={submit} disabled={submitting || !content.replace(/<[^>]*>/g, "").trim()} className="btn-primary mt-3" data-testid="assignment-submit-button">
            {submitting ? "Mengirim..." : submission ? t("course_learn.resubmit_button") : t("course_learn.submit_button")}
          </button>
        </>
      )}
    </div>
  );
}

function CommentsBlock({ lessonId, userName, userPicture, t }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/lessons/${lessonId}/comments`);
    setComments(data);
  }, [lessonId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/lessons/${lessonId}/comments`, { content: text });
      setText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim komentar");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="card-surface p-6 sm:p-8" data-testid="comments-block">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#FF8C00]" />
        <h3 className="font-heading text-xl font-bold text-[#1A1422]">{t("course_learn.comments_title")} ({comments.length})</h3>
      </div>

      <div className="flex gap-3 mb-6">
        {userPicture ? (
          <img src={toAbsUrl(userPicture)} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold flex-shrink-0">
            {userName?.[0] || "U"}
          </div>
        )}
        <div className="flex-1 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("course_learn.comment_placeholder")}
            className="input-field"
            data-testid="comment-input"
            onKeyDown={(e) => e.key === "Enter" && post()}
          />
          <button onClick={post} disabled={posting || !text.trim()} className="btn-primary" data-testid="comment-submit-button">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {comments.length === 0 && (
          <p className="text-[#8B8794] text-sm text-center py-4">{t("course_learn.comments_empty")}</p>
        )}
        {comments.map((c) => (
          <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
            {c.user_picture ? (
              <img src={toAbsUrl(c.user_picture)} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold flex-shrink-0 text-sm">
                {c.user_name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 bg-[#F4F2F8] rounded-2xl px-4 py-2.5">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold text-[#1A1422]">{c.user_name}</span>
                <span className="text-xs text-[#8B8794]">{new Date(c.created_at).toLocaleString("id-ID")}</span>
              </div>
              <p className="text-sm text-[#5C5868] whitespace-pre-line">{c.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
